import React, { useEffect, useMemo, useState } from "react";
import "chart.js/auto";
import Select from "react-select";

import ChartCard from "../common/charts/ChartCard";
import BarChart from "../common/charts/BarChart";
import AreaChart from "../common/charts/AreaChart";
import DoughnutChart from "../common/charts/DoughnutChart";
import PieChart from "../common/charts/PieChart";
import PolarAreaChart from "../common/charts/PolarAreaChart";
import TooltipText from "../common/tooltipText";

const CourseDashboardView = ({
  courseKey,
  stats,
  overall,
  batches,
  subjectWiseStats,
  toMinutes,
}) => {
  const safeStats = stats || {};
  const safeOverall = overall || {};
  const safeBatches =
    Array.isArray(batches) && batches.length > 0
      ? batches
      : Array.isArray(safeStats?.batches)
        ? safeStats.batches
        : [];

  const getSubjectKey = (s) =>
    String(
      s?.subject_key ??
        s?.subjectKey ??
        s?.subject_id ??
        s?.subjectId ??
        s?.id ??
        "",
    );
  const getTopicKey = (t) =>
    String(
      t?.topic_key ?? t?.topicKey ?? t?.topic_id ?? t?.topicId ?? t?.id ?? "",
    );
  const getSubtopicKey = (st) =>
    String(
      st?.subtopic_key ??
        st?.subtopicKey ??
        st?.subtopic_id ??
        st?.subtopicId ??
        st?.id ??
        "",
    );

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);

  const subjectWiseStatsArr = Array.isArray(subjectWiseStats)
    ? subjectWiseStats
    : Array.isArray(safeStats?.subject_wise_stats)
      ? safeStats.subject_wise_stats
      : [];

  const subjectOptions = useMemo(
    () =>
      subjectWiseStatsArr
        .map((s) => ({
          value: getSubjectKey(s),
          label:
            s?.subject_name || s?.subject_key || s?.subjectKey || "Subject",
        }))
        .filter((o) => o.value),
    [subjectWiseStatsArr],
  );

  useEffect(() => {
    setSelectedSubject(subjectOptions?.[0] || null);
    setSelectedTopic(null);
    setSelectedSubtopic(null);
  }, [courseKey, subjectOptions]);

  useEffect(() => {
    if (selectedSubject) return;
    if (!Array.isArray(subjectOptions) || subjectOptions.length === 0) return;
    setSelectedSubject(subjectOptions[0]);
  }, [selectedSubject, subjectOptions]);

  const selectedSubjectObj = useMemo(() => {
    const key = selectedSubject?.value ? String(selectedSubject.value) : "";
    if (!key) return null;
    return subjectWiseStatsArr.find((s) => getSubjectKey(s) === key) || null;
  }, [selectedSubject, subjectWiseStatsArr]);

  const topicsForSelectedSubject = useMemo(() => {
    const topics = selectedSubjectObj?.topics;
    return Array.isArray(topics) ? topics : [];
  }, [selectedSubjectObj]);

  const topicOptions = useMemo(
    () =>
      topicsForSelectedSubject
        .map((t) => ({
          value: getTopicKey(t),
          label: t?.topic_name || t?.topic_key || t?.topicKey || "Topic",
        }))
        .filter((o) => o.value),
    [topicsForSelectedSubject],
  );

  const selectedTopicObj = useMemo(() => {
    const key = selectedTopic?.value ? String(selectedTopic.value) : "";
    if (!key) return null;
    return topicsForSelectedSubject.find((t) => getTopicKey(t) === key) || null;
  }, [selectedTopic, topicsForSelectedSubject]);

  const subtopicsForSelectedTopic = useMemo(() => {
    const subtopics = selectedTopicObj?.subtopics;
    return Array.isArray(subtopics) ? subtopics : [];
  }, [selectedTopicObj]);

  const subtopicOptions = useMemo(
    () =>
      subtopicsForSelectedTopic
        .map((st) => ({
          value: getSubtopicKey(st),
          label:
            st?.subtopic_name ||
            st?.subtopic_key ||
            st?.subtopicKey ||
            "Subtopic",
        }))
        .filter((o) => o.value),
    [subtopicsForSelectedTopic],
  );

  const selectedSubtopicObj = useMemo(() => {
    const key = selectedSubtopic?.value ? String(selectedSubtopic.value) : "";
    if (!key) return null;
    return (
      subtopicsForSelectedTopic.find((st) => getSubtopicKey(st) === key) || null
    );
  }, [selectedSubtopic, subtopicsForSelectedTopic]);

  const distributionLevel = useMemo(() => {
    if (selectedSubtopicObj) return "subtopic";
    if (selectedTopicObj) return "topic";
    if (selectedSubjectObj) return "subject";
    return "all_subjects";
  }, [selectedSubjectObj, selectedSubtopicObj, selectedTopicObj]);

  const activeDistributionSource = useMemo(() => {
    if (selectedSubtopicObj) return [selectedSubtopicObj];
    if (selectedTopicObj) return [selectedTopicObj];
    if (selectedSubjectObj) return [selectedSubjectObj];
    return subjectWiseStatsArr;
  }, [
    selectedSubjectObj,
    selectedSubtopicObj,
    selectedTopicObj,
    subjectWiseStatsArr,
    subtopicsForSelectedTopic,
    topicsForSelectedSubject,
  ]);

  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
      soft: {
        orange: "rgba(252, 164, 59, 0.16)",
        mint: "rgba(133, 238, 173, 0.16)",
        lime: "rgba(212, 249, 45, 0.16)",
        yellow: "rgba(250, 238, 112, 0.16)",
        slate: "rgba(148, 163, 184, 0.16)",
      },
      accents: [
        "rgba(252, 164, 59, 0.85)",
        "rgba(133, 238, 173, 0.85)",
        "rgba(212, 249, 45, 0.85)",
        "rgba(250, 238, 112, 0.85)",
        "rgba(148, 163, 184, 0.65)",
      ],
    }),
    [],
  );

  const sliceColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accents[i % chartPalette.accents.length],
    );

  const [hoveredLegend, setHoveredLegend] = useState(null);

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "left",
        align: "center",
        labels: {
          boxWidth: 12,
          padding: 15,
          font: { size: 11 },
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const meta = chart.getDatasetMeta(0);
                const style = meta.controller.getStyle(i);
                const maxLength = 15;
                const truncatedLabel =
                  label.length > maxLength
                    ? label.substring(0, maxLength) + "..."
                    : label;
                return {
                  text: truncatedLabel,
                  fullText: label,
                  fillStyle: style.backgroundColor,
                  strokeStyle: style.borderColor,
                  lineWidth: style.borderWidth,
                  hidden: !chart.getDataVisibility(i),
                  index: i,
                };
              });
            }
            return [];
          },
        },
        onHover: (event, legendItem) => {
          if (
            legendItem &&
            legendItem.fullText &&
            legendItem.fullText.length > 15
          ) {
            setHoveredLegend({
              text: legendItem.fullText,
              x: event.native.clientX,
              y: event.native.clientY,
            });
          } else {
            setHoveredLegend(null);
          }
        },
        onLeave: () => {
          setHoveredLegend(null);
        },
      },
      tooltip: {
        callbacks: {
          title: (items) => items[0].label,
        },
      },
    },
  };

  const batchCompletionBatches = safeBatches;
  const activeRecallBatches = safeBatches;
  const homeworkBatches = safeBatches;
  const quizzesBatches = safeBatches;
  const testsBatches = safeBatches;
  const adaptiveBatches = safeBatches;
  const timeSpentBatches = safeBatches;
  const coinsBatches = safeBatches;
  const xpBatches = safeBatches;

  // console.log("Mapped Batches Examples:", {
  //   batchCompletionBatches: batchCompletionBatches.length,
  //   firstBatch: batchCompletionBatches[0]
  // });

  const batchCompletionLeaderboard = Array.isArray(
    safeStats?.batch_completion?.leaderboard,
  )
    ? safeStats.batch_completion.leaderboard
    : [];

  const activeRecallLeaderboard = Array.isArray(
    safeStats?.active_recall?.leaderboard,
  )
    ? safeStats.active_recall.leaderboard
    : [];

  const homeworkLeaderboard = Array.isArray(safeStats?.homework?.leaderboard)
    ? safeStats.homework.leaderboard
    : [];

  const quizzesLeaderboard = Array.isArray(safeStats?.quizzes?.leaderboard)
    ? safeStats.quizzes.leaderboard
    : [];

  const testsLeaderboard = Array.isArray(safeStats?.tests?.leaderboard)
    ? safeStats.tests.leaderboard
    : [];

  const adaptiveLeaderboard = Array.isArray(
    safeStats?.adaptive_learning?.leaderboard,
  )
    ? safeStats.adaptive_learning.leaderboard
    : [];

  const timeSpentLeaderboard = Array.isArray(safeStats?.time_spent?.leaderboard)
    ? safeStats.time_spent.leaderboard
    : [];

  const coinsLeaderboard = Array.isArray(safeStats?.coins?.leaderboard)
    ? safeStats.coins.leaderboard
    : [];

  const xpLeaderboard = Array.isArray(safeStats?.xp?.leaderboard)
    ? safeStats.xp.leaderboard
    : [];

  const buildDistribution = (key) => {
    const totals = {
      above_70: 0,
      above_50: 0,
      below_50: 0,
      not_attended: 0,
    };

    activeDistributionSource.forEach((s) => {
      const dist = s && typeof s === "object" ? s[key] : null;
      if (!dist || typeof dist !== "object") return;
      totals.above_70 += Number(dist.above_70) || 0;
      totals.above_50 += Number(dist.above_50) || 0;
      totals.below_50 += Number(dist.below_50) || 0;
      totals.not_attended += Number(dist.not_attended) || 0;
    });

    const sum =
      totals.above_70 + totals.above_50 + totals.below_50 + totals.not_attended;

    if (!sum) {
      return {
        labels: ["No data"],
        datasets: [
          {
            data: [1],
            backgroundColor: [chartPalette.solid.slate],
            hoverBackgroundColor: [chartPalette.hover.slate],
            borderColor: "rgba(255, 255, 255, 0.9)",
            borderWidth: 1,
          },
        ],
      };
    }

    return {
      labels: ["Above 70", "Above 50", "Below 50", "Not Attended"],
      datasets: [
        {
          data: [
            totals.above_70,
            totals.above_50,
            totals.below_50,
            totals.not_attended,
          ],
          backgroundColor: sliceColors(4),
          borderColor: "rgba(255, 255, 255, 0.9)",
          borderWidth: 1,
        },
      ],
    };
  };

  const cards = [
    { label: "Total Students", value: safeOverall.total_students ?? 0 },
    { label: "Total Batches", value: safeOverall.total_batches ?? 0 },
    {
      label: "Total Homework Assigned",
      value: safeOverall.total_homework_assigned ?? 0,
    },
    {
      label: "Total Overall Tests",
      value: safeOverall.total_overall_tests ?? 0,
    },
  ].filter((c) => c.value !== "");

  return (
    <section className="dashboard-course-section">
      <section className="dashboard-overview-section">
        <h2>Overview</h2>
        <div className="overview-grid">
          {cards.map((c) => (
            <div key={c.label} className="card overview-card">
              <div className="overview-card-content">
                <div className="overview-card-text">
                  <TooltipText
                    text={c.label}
                    className="overview-card-label"
                    fullWidth
                  />
                  <strong>{c.value}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="dashboard-course-grid">
        <ChartCard title="Course Completion (%)">
          <AreaChart
            data={{
              labels: batchCompletionBatches.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Completion (%)",
                  data: batchCompletionBatches.map(
                    (b) => Number(b.completion_percentage) || 0,
                  ),
                  // borderColor: "rgba(99, 102, 241, 1)",
                  // backgroundColor: chartPalette.soft.mint,
                  borderColor: chartPalette.solid.orange,
                  backgroundColor: chartPalette.soft.orange,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Active Recall">
          {(() => {
            const completedArr = activeRecallBatches.map(
              (b) => Number(b.total_active_recall_completed) || 0,
            );
            const generatedTotalArr = activeRecallBatches.map(
              (b) => Number(b.total_active_recall_generated) || 0,
            );
            const generatedRemainingArr = generatedTotalArr.map((g, i) =>
              Math.max(0, g - (completedArr[i] || 0)),
            );

            return (
              <BarChart
                data={{
                  labels: activeRecallBatches.map((b) => b.batch_name),
                  datasets: [
                    {
                      label: "Completed",
                      data: completedArr,
                      backgroundColor: chartPalette.solid.lime,
                      hoverBackgroundColor: chartPalette.hover.lime,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                    {
                      label: "Generated",
                      data: generatedRemainingArr,
                      backgroundColor: chartPalette.soft.slate,
                      hoverBackgroundColor: chartPalette.soft.slate,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { stacked: true },
                    y: { beginAtZero: true, stacked: true },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const idx = context.dataIndex;
                          if (context.dataset.label === "Generated") {
                            return ` Generated: ${generatedTotalArr[idx] || 0}`;
                          }
                          return ` Completed: ${completedArr[idx] || 0}`;
                        },
                      },
                    },
                  },
                }}
              />
            );
          })()}
        </ChartCard>

        <ChartCard title="Homework Completion">
          {(() => {
            const completedArr = homeworkBatches.map(
              (b) => Number(b.total_homework_completed) || 0,
            );
            const assignedTotalArr = homeworkBatches.map(
              (b) => Number(b.total_homework_assigned) || 0,
            );
            const assignedRemainingArr = assignedTotalArr.map((a, i) =>
              Math.max(0, a - (completedArr[i] || 0)),
            );

            return (
              <BarChart
                data={{
                  labels: homeworkBatches.map((b) => b.batch_name),
                  datasets: [
                    {
                      label: "Completed",
                      data: completedArr,
                      backgroundColor: chartPalette.solid.mint,
                      hoverBackgroundColor: chartPalette.hover.mint,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                    {
                      label: "Assigned",
                      data: assignedRemainingArr,
                      backgroundColor: chartPalette.soft.slate,
                      hoverBackgroundColor: chartPalette.soft.slate,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { stacked: true },
                    y: {
                      beginAtZero: true,
                      stacked: true,
                      title: { display: true, text: "Slots" },
                    },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const idx = context.dataIndex;
                          if (context.dataset.label === "Assigned") {
                            return ` Assigned: ${assignedTotalArr[idx] || 0}`;
                          }
                          return ` Completed: ${completedArr[idx] || 0}`;
                        },
                        afterBody: (items) => {
                          const idx = items[0].dataIndex;
                          const batch = homeworkBatches[idx];
                          if (batch) {
                            return `Completion Rate: ${batch.homework_completion_rate}%`;
                          }
                          return "";
                        },
                      },
                    },
                  },
                }}
              />
            );
          })()}
        </ChartCard>

        <ChartCard title="Quiz Completion">
          {(() => {
            const completedArr = quizzesBatches.map(
              (b) => Number(b.total_quiz_fully_completed) || 0,
            );
            const assignedTotalArr = quizzesBatches.map(
              (b) => Number(b.total_quiz_assigned) || 0,
            );
            const assignedRemainingArr = assignedTotalArr.map((a, i) =>
              Math.max(0, a - (completedArr[i] || 0)),
            );

            return (
              <BarChart
                data={{
                  labels: quizzesBatches.map((b) => b.batch_name),
                  datasets: [
                    {
                      label: "Completed",
                      data: completedArr,
                      backgroundColor: chartPalette.solid.lime,
                      hoverBackgroundColor: chartPalette.hover.lime,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                    {
                      label: "Assigned",
                      data: assignedRemainingArr,
                      backgroundColor: chartPalette.soft.slate,
                      hoverBackgroundColor: chartPalette.soft.slate,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { stacked: true },
                    y: {
                      beginAtZero: true,
                      stacked: true,
                      title: { display: true, text: "Slots" },
                    },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const idx = context.dataIndex;
                          if (context.dataset.label === "Assigned") {
                            return ` Assigned: ${assignedTotalArr[idx] || 0}`;
                          }
                          return ` Completed: ${completedArr[idx] || 0}`;
                        },
                        afterBody: (items) => {
                          const idx = items[0].dataIndex;
                          const batch = quizzesBatches[idx];
                          if (batch) {
                            return `Completion Rate: ${batch.quiz_completion_rate}%`;
                          }
                          return "";
                        },
                      },
                    },
                  },
                }}
              />
            );
          })()}
        </ChartCard>

        <ChartCard title="Test Completion">
          {(() => {
            const completedArr = testsBatches.map(
              (b) => Number(b.total_test_fully_completed) || 0,
            );
            const assignedTotalArr = testsBatches.map(
              (b) => Number(b.total_test_assigned) || 0,
            );
            const assignedRemainingArr = assignedTotalArr.map((a, i) =>
              Math.max(0, a - (completedArr[i] || 0)),
            );

            return (
              <BarChart
                data={{
                  labels: testsBatches.map((b) => b.batch_name),
                  datasets: [
                    {
                      label: "Completed",
                      data: completedArr,
                      backgroundColor: chartPalette.solid.orange,
                      hoverBackgroundColor: chartPalette.hover.orange,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                    {
                      label: "Assigned",
                      data: assignedRemainingArr,
                      backgroundColor: chartPalette.soft.slate,
                      hoverBackgroundColor: chartPalette.soft.slate,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { stacked: true },
                    y: {
                      beginAtZero: true,
                      stacked: true,
                      title: { display: true, text: "Slots" },
                    },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const idx = context.dataIndex;
                          if (context.dataset.label === "Assigned") {
                            return ` Assigned: ${assignedTotalArr[idx] || 0}`;
                          }
                          return ` Completed: ${completedArr[idx] || 0}`;
                        },
                        afterBody: (items) => {
                          const idx = items[0].dataIndex;
                          const batch = testsBatches[idx];
                          if (batch) {
                            return `Completion Rate: ${batch.test_completion_rate}%`;
                          }
                          return "";
                        },
                      },
                    },
                  },
                }}
              />
            );
          })()}
        </ChartCard>

        <ChartCard title="Adaptive Tests">
          {(() => {
            const completedArr = adaptiveBatches.map(
              (b) => Number(b.total_adaptive_tests_completed) || 0,
            );
            const totalArr = adaptiveBatches.map(
              (b) => Number(b.total_adaptive_tests) || 0,
            );
            const remainingArr = totalArr.map((t, i) =>
              Math.max(0, t - (completedArr[i] || 0)),
            );

            return (
              <BarChart
                data={{
                  labels: adaptiveBatches.map((b) => b.batch_name),
                  datasets: [
                    {
                      label: "Completed",
                      data: completedArr,
                      backgroundColor: chartPalette.solid.yellow,
                      hoverBackgroundColor: chartPalette.hover.yellow,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                    {
                      label: "Total",
                      data: remainingArr,
                      backgroundColor: chartPalette.soft.slate,
                      hoverBackgroundColor: chartPalette.soft.slate,
                      borderRadius: 10,
                      stack: "stack1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { stacked: true },
                    y: { beginAtZero: true, stacked: true },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const idx = context.dataIndex;
                          if (context.dataset.label === "Total") {
                            return ` Total: ${totalArr[idx] || 0}`;
                          }
                          return ` Completed: ${completedArr[idx] || 0}`;
                        },
                      },
                    },
                  },
                }}
              />
            );
          })()}
        </ChartCard>

        <ChartCard title="Time Spent (minutes)">
          <AreaChart
            data={{
              labels: timeSpentBatches.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Time Spent (min)",
                  data: timeSpentBatches.map((b) =>
                    toMinutes ? toMinutes(b.total_time_spent_seconds) : 0,
                  ),
                  borderColor: chartPalette.solid.orange,
                  backgroundColor: chartPalette.soft.orange,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true } },
            }}
          />
        </ChartCard>

        <ChartCard title="Coins Earned">
          <BarChart
            data={{
              labels: coinsBatches.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Coins Earned",
                  data: coinsBatches.map((b) => Number(b.total_coins) || 0),
                  backgroundColor: chartPalette.solid.yellow,
                  hoverBackgroundColor: chartPalette.hover.yellow,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true } },
            }}
          />
        </ChartCard>

        <ChartCard title="XP Earned">
          <AreaChart
            data={{
              labels: xpBatches.map((b) => b.batch_name),
              datasets: [
                {
                  label: "XP Earned",
                  data: xpBatches.map((b) => Number(b.total_xp) || 0),
                  borderColor: chartPalette.solid.lime,
                  backgroundColor: chartPalette.soft.lime,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true } },
            }}
          />
        </ChartCard>
      </div>

      <div className="course-select-row">
        <label>Subject</label>
        <div className="course-select-wrapper">
          <Select
            value={selectedSubject}
            onChange={(v) => {
              setSelectedSubject(v);
              setSelectedTopic(null);
              setSelectedSubtopic(null);
            }}
            options={subjectOptions}
            isClearable
            placeholder="Select subject"
          />
        </div>

        <label>Topic</label>
        <div className="course-select-wrapper">
          <Select
            value={selectedTopic}
            onChange={(v) => {
              setSelectedTopic(v);
              setSelectedSubtopic(null);
            }}
            options={topicOptions}
            isClearable
            isDisabled={!selectedSubject}
            placeholder="Select topic"
          />
        </div>

        <label>Subtopic</label>
        <div className="course-select-wrapper">
          <Select
            value={selectedSubtopic}
            onChange={(v) => setSelectedSubtopic(v)}
            options={subtopicOptions}
            isClearable
            isDisabled={!selectedTopic}
            placeholder="Select subtopic"
          />
        </div>
      </div>

      <>
        <h2>
          {distributionLevel === "subtopic"
            ? "Subtopic-wise Distributions"
            : distributionLevel === "topic"
              ? "Topic-wise Distributions"
              : "Subject-wise Distributions"}
        </h2>
        <div className="dashboard-course-grid">
          <ChartCard title="Test Score Distribution">
            <PieChart
              data={buildDistribution("test_score_distribution")}
              options={commonChartOptions}
            />
          </ChartCard>

          <ChartCard title="Quiz Score Distribution">
            <PieChart
              data={buildDistribution("quiz_score_distribution")}
              options={commonChartOptions}
            />
          </ChartCard>

          <ChartCard title="Adaptive Score Distribution">
            <PieChart
              data={buildDistribution("adaptive_score_distribution")}
              options={commonChartOptions}
            />
          </ChartCard>

          <ChartCard title="Active Recall Score Distribution">
            <PieChart
              data={buildDistribution("active_recall_score_distribution")}
              options={commonChartOptions}
            />
          </ChartCard>
        </div>
      </>

      <h2>Leaderboards</h2>
      <div className="dashboard-course-grid">
        <ChartCard title="Course Completion (%)">
          <DoughnutChart
            data={{
              labels: batchCompletionLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Completion (%)",
                  data: batchCompletionLeaderboard.map(
                    (b) => Number(b.completion_percentage) || 0,
                  ),
                  backgroundColor: sliceColors(
                    batchCompletionLeaderboard.length,
                  ),
                  borderColor: "rgba(255, 255, 255, 0.9)",
                  borderWidth: 1,
                },
              ],
            }}
            options={commonChartOptions}
          />
        </ChartCard>

        <ChartCard title="Active Recall">
          <BarChart
            data={{
              labels: activeRecallLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Active Recall Completion (%)",
                  data: activeRecallLeaderboard.map((b) => {
                    const direct = Number(b.active_recall_completion_rate);
                    if (Number.isFinite(direct)) return direct;
                    const completed =
                      Number(b.total_active_recall_completed) || 0;
                    const generated =
                      Number(b.total_active_recall_generated) || 0;
                    return generated > 0 ? (completed / generated) * 100 : 0;
                  }),
                  backgroundColor: chartPalette.solid.lime,
                  hoverBackgroundColor: chartPalette.hover.lime,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Homework Completion Rate (%)">
          <BarChart
            data={{
              labels: homeworkLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Homework Completion (%)",
                  data: homeworkLeaderboard.map(
                    (b) => Number(b.homework_completion_rate) || 0,
                  ),
                  backgroundColor: chartPalette.solid.mint,
                  hoverBackgroundColor: chartPalette.hover.mint,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Quiz Completion Rate (%)">
          <BarChart
            data={{
              labels: quizzesLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Quiz Completion (%)",
                  data: quizzesLeaderboard.map(
                    (b) => Number(b.quiz_completion_rate) || 0,
                  ),
                  backgroundColor: chartPalette.solid.yellow,
                  hoverBackgroundColor: chartPalette.hover.yellow,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Test Completion Rate (%)">
          <BarChart
            data={{
              labels: testsLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Test Completion (%)",
                  data: testsLeaderboard.map(
                    (b) => Number(b.test_completion_rate) || 0,
                  ),
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Adaptive">
          <BarChart
            data={{
              labels: adaptiveLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Adaptive Completion (%)",
                  data: adaptiveLeaderboard.map((b) => {
                    const direct = Number(b.adaptive_completion_rate);
                    if (Number.isFinite(direct)) return direct;
                    const completed =
                      Number(b.total_adaptive_tests_completed) || 0;
                    const total = Number(b.total_adaptive_tests) || 0;
                    return total > 0 ? (completed / total) * 100 : 0;
                  }),
                  backgroundColor: chartPalette.solid.mint,
                  hoverBackgroundColor: chartPalette.hover.mint,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Coins Earned">
          <PieChart
            data={{
              labels: coinsLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Coins Earned",
                  data: coinsLeaderboard.map((b) => Number(b.total_coins) || 0),
                  backgroundColor: sliceColors(coinsLeaderboard.length),
                  borderColor: "rgba(255, 255, 255, 0.9)",
                  borderWidth: 1,
                },
              ],
            }}
            options={commonChartOptions}
          />
        </ChartCard>

        <ChartCard title="XP Earned">
          <DoughnutChart
            data={{
              labels: xpLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "XP Earned",
                  data: xpLeaderboard.map((b) => Number(b.total_xp) || 0),
                  backgroundColor: sliceColors(xpLeaderboard.length),
                  borderColor: "rgba(255, 255, 255, 0.9)",
                  borderWidth: 1,
                },
              ],
            }}
            options={commonChartOptions}
          />
        </ChartCard>

        <ChartCard title="Time Spent (min)">
          <BarChart
            data={{
              labels: timeSpentLeaderboard.map((b) => b.batch_name),
              datasets: [
                {
                  label: "Time Spent (min)",
                  data: timeSpentLeaderboard.map((b) =>
                    toMinutes ? toMinutes(b.total_time_spent_seconds) : 0,
                  ),
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true } },
            }}
          />
        </ChartCard>
      </div>

      {hoveredLegend && (
        <div
          className="chart-legend-tooltip"
          style={{
            left: hoveredLegend.x + 15,
            top: hoveredLegend.y - 10,
          }}
        >
          {hoveredLegend.text}
        </div>
      )}
    </section>
  );
};

export default CourseDashboardView;
